import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Briefcase, ChevronUp, X, ExternalLink, Search, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActiveMatter, validateActiveMatter } from '../lib/activeMatter';

const HIDDEN = ['/', '/auth', '/login', '/register', '/fidem'];

// Floating "causa activa" switcher: the chosen case follows the user through CRM, Cerebro and the AI agents
export const ActiveMatterBar = () => {
  const { user, api } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useActiveMatter();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  if (!user || HIDDEN.includes(pathname) || pathname.startsWith('/legal/') || pathname.startsWith('/sign')) return null;
  const toggle = () => {
    if (!open && !list) api.get('/crm/matters', { params: { limit: 100 } }).then((r) => { const ms = r.data.matters || []; setList(ms); validateActiveMatter(ms); }).catch(() => setList([]));
    setOpen(!open);
  };
  const shown = (list || []).filter((m) => m.title.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
  return (
    <div className={`fixed bottom-[10.5rem] left-3 md:bottom-16 ${open ? 'z-[55]' : 'z-40'}`} data-testid="active-matter-bar">
      {open && (
        <div data-testid="active-matter-menu" className="mb-2 w-72 rounded-xl border border-slate-700 bg-slate-900/95 p-3 shadow-2xl backdrop-blur-xl">
          <p className="mb-2 text-xs font-semibold text-white">Causa activa</p>
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950 px-2">
            <Search className="h-3.5 w-3.5 text-slate-500" />
            <input data-testid="active-matter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar causa..." className="w-full bg-transparent py-1.5 text-xs text-slate-100 outline-none" />
          </div>
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {!list && <p className="text-xs text-slate-500">Cargando...</p>}
            {list && !shown.length && <p className="text-xs text-slate-500">Sin causas. Créala en el CRM.</p>}
            {shown.map((m) => (
              <button key={m.id} data-testid={`active-matter-option-${m.id}`} onClick={() => { setActive(m); setOpen(false); }}
                className={`w-full rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${active?.id === m.id ? 'bg-amber-500/15 text-amber-200' : 'text-slate-300 hover:bg-slate-800'}`}>
                <span className="block truncate font-medium">{m.title}</span><span className="text-[10px] capitalize text-slate-500">{m.matter_type} · {m.country}</span>
              </button>
            ))}
          </div>
          {active && (
            <div className="mt-2 flex gap-2 border-t border-slate-800 pt-2">
              <button data-testid="active-matter-open" onClick={() => { navigate(`/crm?caso=${active.id}`); setOpen(false); }} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-amber-500 py-1.5 text-xs font-semibold text-slate-950"><ExternalLink className="h-3 w-3" />Abrir en CRM</button>
              <button data-testid="active-matter-fidem" onClick={() => { navigate('/fidem'); setOpen(false); }} className="flex items-center gap-1 rounded-lg border border-amber-500/50 px-2 text-xs font-semibold text-amber-200"><Sparkles className="h-3 w-3" />Fidem</button>
              <button data-testid="active-matter-clear" onClick={() => { setActive(null); setOpen(false); }} className="rounded-lg border border-slate-700 px-2 text-xs text-slate-400 hover:text-white"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      )}
      <button data-testid="active-matter-toggle" onClick={toggle} title={active ? active.title : 'Elegir causa activa'}
        className={`flex max-w-[16rem] items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-lg backdrop-blur-xl transition-colors ${active ? 'border-amber-500/60 bg-slate-900/90 text-amber-200' : 'border-slate-700 bg-slate-900/80 text-slate-400'}`}>
        <Briefcase className="h-3.5 w-3.5 shrink-0" /><span data-testid="active-matter-title" className="truncate">{active ? active.title : 'Elegir causa activa'}</span><ChevronUp className={`h-3 w-3 shrink-0 transition-transform ${open ? '' : 'rotate-180'}`} />
      </button>
    </div>
  );
};
